import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Pagination,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { teachersService } from '../../services/teachers';
import { getCountries, getStates, getCities } from '../../data/locationData';
import {
  getAllPhoneCodes,
  getDialCodeFromSelectValue,
  getPhoneCodeSelectValueForCountry,
  resolvePhoneCodeSelectValue,
} from '../../data/phoneCodes';
import { getTimezone, getCountryFromTimezone } from '../../data/timezones';
import { getAvailableTimezones } from '../../utils/timezoneUtils';
import DayScheduleSection from '../../components/DayScheduleSection';
import TeacherDeactivateDialog from '../../components/Teacher/TeacherDeactivateDialog';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const Teachers = () => {
  const { isAdmin, canManageTeachers } = useAuth();
  const canEditTeachers = canManageTeachers();
  const canDeleteTeachers = isAdmin();
  const phoneCodeOptions = getAllPhoneCodes();
  const phoneCodesForParsing = phoneCodeOptions
    .map((opt) => opt.code)
    .sort((a, b) => b.length - a.length);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create | edit | view
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', status: 'active' });
  const [searchInput, setSearchInput] = useState('');
  const [deactivateTeacher, setDeactivateTeacher] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneCode: getPhoneCodeSelectValueForCountry('India'),
    phone: '',
    country: '',
    state: '',
    city: '',
    address: '',
    demoReady: '',
    demoCountry: '',
    monthlyDemo: '',
    zoomMeetingUrl: '',
  });

  // State for cascading dropdowns
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  
  // State for daily work hours (multiple time slots)
  const [dailyWorkHours, setDailyWorkHours] = useState({
    monday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    tuesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    wednesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    thursday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    friday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    saturday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    sunday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
  });
  
  // Single timezone for all work hours
  const [workHoursTimezone, setWorkHoursTimezone] = useState('UTC');
  
  // Validation errors state (reserved for future per-field UI messaging)
  // const [validationErrors, setValidationErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const fetchTeachers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await teachersService.getTeachers({
        page,
        limit,
        search: (filters.search || '').trim() || undefined,
        status: filters.status || 'active',
      });
      setTeachers(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      setError('Failed to fetch teachers');
      console.error('Error fetching teachers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters.search, filters.status]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput, }));
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleOpenDialog = (teacher = null, mode = 'create') => {
    // Clear any previous messages
    setFormError('');
    setFormSuccess('');
    setDialogMode(teacher ? mode : 'create');

    if (teacher) {
      setEditingTeacher(teacher);
      
      // Extract phone code and number from existing phone
      let dialCode = '+91'; // Default
      let phoneNumber = '';
      if (teacher.phone) {
        // Try to extract country code from phone number
        for (const code of phoneCodesForParsing) {
          if (teacher.phone.startsWith(code)) {
            dialCode = code;
            phoneNumber = teacher.phone.substring(code.length);
            break;
          }
        }
        if (!phoneNumber) {
          phoneNumber = teacher.phone; // If no code found, use as is
        }
      }
      
      const teacherData = {
        firstName: teacher.first_name || '',
        lastName: teacher.last_name || '',
        email: teacher.email || '',
        phoneCode: resolvePhoneCodeSelectValue(dialCode, teacher.country || ''),
        phone: phoneNumber,
        country: teacher.country || '',
        state: teacher.state || '',
        city: teacher.city || '',
        address: teacher.address || '',
        demoReady: teacher.demo_ready || '',
        demoCountry: teacher.demo_countries && teacher.demo_countries.length > 0 
          ? teacher.demo_countries 
          : (teacher.demo_country ? [teacher.demo_country] : []),
        demoCountries: teacher.demo_countries || (teacher.demo_country ? [teacher.demo_country] : []),
        monthlyDemo: teacher.monthly_demo || '',
        zoomMeetingUrl: teacher.zoom_meeting_url || '',
      };
      setFormData(teacherData);
      
      // Populate cascading dropdowns for editing
      if (teacherData.country) {
        const states = getStates(teacherData.country);
        setAvailableStates(states);
        if (teacherData.state) {
          const cities = getCities(teacherData.country, teacherData.state);
          setAvailableCities(cities);
        }
        // Auto-set timezone based on country (and state if available)
        const timezone = getTimezone(teacherData.country, teacherData.state);
        setWorkHoursTimezone(timezone);
      }

      // Prefill availability/time slots by fetching teacher details
      teachersService.getTeacher(teacher.id).then((res) => {
        const avails = res.data?.availability || [];
        const dayMapping = { 0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'saturday' };
        // Use timezone from saved availability if available, otherwise use country-based timezone
        const defaultTimezone = avails.length > 0 && avails[0].timezone 
          ? avails[0].timezone 
          : (teacherData.country ? getTimezone(teacherData.country, teacherData.state) : 'UTC');
        
        const newWorkHours = {
          monday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
          tuesday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
          wednesday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
          thursday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
          friday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
          saturday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
          sunday: { timezone: defaultTimezone, slots: [], totalDayHours: 0 },
        };
        avails.forEach((slot) => {
          const dayName = dayMapping[slot.day_of_week];
          if (!dayName) return;
          const slots = newWorkHours[dayName].slots || [];
          slots.push({
            startTime: (slot.start_time || '').slice(0, 5),
            endTime: (slot.end_time || '').slice(0, 5),
            totalHours: Number(slot.total_hours) || 0,
            isActive: slot.is_available,
          });
          newWorkHours[dayName] = {
            timezone: slot.timezone || defaultTimezone,
            slots,
            totalDayHours: slots.reduce((sum, s) => sum + (s.totalHours || 0), 0),
          };
        });
        setDailyWorkHours(newWorkHours);
        if (avails.length > 0 && avails[0].timezone) {
          setWorkHoursTimezone(avails[0].timezone);
        } else if (teacherData.country) {
          // If no saved availability, use country-based timezone
          setWorkHoursTimezone(getTimezone(teacherData.country, teacherData.state));
        }
      }).catch((err) => {
        console.error('Failed to load availability', err);
      });
    } else {
      setEditingTeacher(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneCode: getPhoneCodeSelectValueForCountry('India'),
        phone: '',
        country: '',
        state: '',
        city: '',
        address: '',
        demoReady: '',
        demoCountry: [],
        demoCountries: [],
        monthlyDemo: '',
        zoomMeetingUrl: '',
      });
      // Reset cascading dropdowns
      setAvailableStates([]);
      setAvailableCities([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeacher(null);
    setDialogMode('create');
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneCode: getPhoneCodeSelectValueForCountry('India'),
      phone: '',
      country: '',
      state: '',
      city: '',
      address: '',
      demoReady: '',
      demoCountry: [],
      demoCountries: [],
      monthlyDemo: '',
      zoomMeetingUrl: '',
    });
    // Reset cascading dropdowns
    setAvailableStates([]);
    setAvailableCities([]);
    // Reset daily work hours (multiple slots)
    setDailyWorkHours({
      monday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      tuesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      wednesday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      thursday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      friday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      saturday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
      sunday: { timezone: 'UTC', slots: [], totalDayHours: 0 },
    });
    // Reset timezone
    setWorkHoursTimezone('UTC');
    // Clear form messages
    setFormError('');
    setFormSuccess('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Handle cascading dropdowns
    if (field === 'country') {
      const states = getStates(value);
      setAvailableStates(states);
      // Auto-select phone code based on country (unique value so +1 USA ≠ Canada)
      const phoneCode = getPhoneCodeSelectValueForCountry(value);
      // Auto-select timezone based on country
      const timezone = getTimezone(value);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        state: '', // Reset state when country changes
        city: '',   // Reset city when country changes
        phoneCode // Auto-select phone code
      }));
      setAvailableCities([]); // Reset cities
      
      // Update timezone for all work hours
      setWorkHoursTimezone(timezone);
      
      // Update timezone in all daily work hours slots
      setDailyWorkHours(prev => {
        const updated = {};
        Object.keys(prev).forEach(day => {
          updated[day] = {
            ...prev[day],
            timezone: timezone
          };
        });
        return updated;
      });
    } else if (field === 'state') {
      const cities = getCities(formData.country, value);
      setAvailableCities(cities);
      // Auto-select timezone based on country and state
      const timezone = getTimezone(formData.country, value);
      // Update timezone for all work hours
      setWorkHoursTimezone(timezone);
      
      // Update timezone in all daily work hours slots
      setDailyWorkHours(prev => {
        const updated = {};
        Object.keys(prev).forEach(day => {
          updated[day] = {
            ...prev[day],
            timezone: timezone
          };
        });
        return updated;
      });
      setFormData(prev => ({
        ...prev,
        [field]: value,
        city: '' // Reset city when state changes
      }));
      
      // Update timezone for all work hours
      setWorkHoursTimezone(timezone);
    }
  };

  // Multiple time slots handlers
  const handleAddTimeSlot = (dayName) => {
    setDailyWorkHours(prev => {
      const updated = { ...prev };
      const dayData = { ...updated[dayName] };
      
      const newSlot = {
        id: Date.now() + Math.random(), // Simple unique ID
        startTime: '09:00',
        endTime: '10:00',
        totalHours: 1,
        isActive: true
      };
      
      dayData.slots = [...dayData.slots, newSlot];
      updated[dayName] = dayData;
      
      // Recalculate total hours
      recalculateDayHours(updated, dayName);
      
      return updated;
    });
  };

  const handleUpdateTimeSlot = (dayName, slotIndex, field, value) => {
    setDailyWorkHours(prev => {
      const updated = { ...prev };
      const dayData = { ...updated[dayName] };
      const slots = [...dayData.slots];
      
      if (field === 'startTime' || field === 'endTime') {
        slots[slotIndex] = { ...slots[slotIndex], [field]: value };
        // Auto-calculate hours
        if (slots[slotIndex].startTime && slots[slotIndex].endTime) {
          const start = new Date(`2000-01-01T${slots[slotIndex].startTime}`);
          const end = new Date(`2000-01-01T${slots[slotIndex].endTime}`);
          if (end > start) {
            const hours = (end - start) / (1000 * 60 * 60);
            slots[slotIndex].totalHours = Math.round(hours * 10) / 10;
          }
        }
      } else {
        slots[slotIndex] = { ...slots[slotIndex], [field]: value };
      }
      
      dayData.slots = slots;
      updated[dayName] = dayData;
      
      // Recalculate total hours
      recalculateDayHours(updated, dayName);
      
      return updated;
    });
  };

  const handleDeleteTimeSlot = (dayName, slotIndex) => {
    setDailyWorkHours(prev => {
      const updated = { ...prev };
      const dayData = { ...updated[dayName] };
      dayData.slots = dayData.slots.filter((_, index) => index !== slotIndex);
      updated[dayName] = dayData;
      
      // Recalculate total hours
      recalculateDayHours(updated, dayName);

      return updated;
    });
  };

  const recalculateDayHours = (workHours, dayName) => {
    const dayData = workHours[dayName];
    const totalHours = dayData.slots
      .filter(slot => slot.isActive)
      .reduce((sum, slot) => sum + slot.totalHours, 0);
    
    workHours[dayName] = {
      ...dayData,
      totalDayHours: totalHours
    };
  };

  // Legacy handler for backward compatibility (unused, kept for reference)
  // const handleWorkHoursChange = (day, field, value) => {};



  const handleSubmit = async () => {
    if (dialogMode === 'view') return;
    // Clear previous messages
    setFormError('');
    setFormSuccess('');
    
    try {
      // Client-side validation for work hours (multiple slots)
      const validationErrors = [];

      if (!formData.country) {
        validationErrors.push('Country is required');
      }
      if (!workHoursTimezone) {
        validationErrors.push('Work hours timezone is required');
      }
      
      // Check for overlapping slots and validate each slot
      Object.entries(dailyWorkHours).forEach(([day, data]) => {
        const activeSlots = data.slots.filter(slot => slot.isActive);
        
        if (activeSlots.length > 1) {
          // Sort by start time
          const sortedSlots = [...activeSlots].sort((a, b) => a.startTime.localeCompare(b.startTime));
          
          for (let i = 0; i < sortedSlots.length - 1; i++) {
            const current = sortedSlots[i];
            const next = sortedSlots[i + 1];
            
            if (current.endTime > next.startTime) {
              validationErrors.push(`${day.charAt(0).toUpperCase() + day.slice(1)}: Time slots overlap (${current.startTime}-${current.endTime} and ${next.startTime}-${next.endTime})`);
            }
          }
        }
        
        // Validate individual slots
        activeSlots.forEach((slot, index) => {
          if (!slot.startTime || !slot.endTime) {
            validationErrors.push(`${day.charAt(0).toUpperCase() + day.slice(1)} Slot ${index + 1}: Start and end times are required`);
          } else {
            const start = new Date(`2000-01-01T${slot.startTime}`);
            const end = new Date(`2000-01-01T${slot.endTime}`);
            if (start >= end) {
              validationErrors.push(`${day.charAt(0).toUpperCase() + day.slice(1)} Slot ${index + 1}: End time must be after start time`);
          }
        }
        });
      });
      
      if (validationErrors.length > 0) {
        setFormError(`Please fix the following issues:\n${validationErrors.join('\n')}`);
        return;
      }

      // Clean optional fields to avoid sending empty strings
      const clean = (val) => (val === '' ? undefined : val);
      const teacherData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: clean(formData.phone)
          ? getDialCodeFromSelectValue(formData.phoneCode) + formData.phone
          : undefined,
        country: formData.country,
        state: clean(formData.state),
        city: clean(formData.city),
        address: clean(formData.address),
        demo_ready: clean(formData.demoReady),
        demo_countries: formData.demoCountry && formData.demoCountry.length > 0 ? formData.demoCountry : undefined,
        monthly_demo: clean(formData.monthlyDemo),
        timezone: workHoursTimezone, // Include the selected timezone
        zoom_meeting_url: clean(formData.zoomMeetingUrl),
      };

      // Build work hours payload once
      const workHoursData = [];
      const dayMapping = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 };
      Object.entries(dailyWorkHours).forEach(([dayName, dayData]) => {
        const dayOfWeek = dayMapping[dayName];
        dayData.slots.forEach(slot => {
          if (slot.isActive && slot.totalHours > 0) {
            workHoursData.push({
              day_of_week: dayOfWeek,
              total_hours: slot.totalHours,
              start_time: slot.startTime,
              end_time: slot.endTime,
              timezone: dayData.timezone || workHoursTimezone,
              is_available: slot.isActive
            });
          }
        });
      });

      if (editingTeacher) {
        await teachersService.updateTeacher(editingTeacher.id, teacherData);
        if (workHoursData.length > 0) {
          await teachersService.updateAvailability(editingTeacher.id, workHoursData);
        }
        setFormSuccess(workHoursData.length > 0 ? 'Teacher and work hours updated successfully' : 'Teacher updated successfully');
        setTimeout(() => {
          handleCloseDialog();
          fetchTeachers();
        }, 2000);
      } else {
        const response = await teachersService.createTeacher(teacherData);
        const creds = response.credentials;
        const credentialNote = creds
          ? ` Login: ${creds.email} / Password: ${creds.password}`
          : '';
        setFormSuccess(`Teacher created successfully.${credentialNote}`);
        
        // After creating teacher, save daily work hours (multiple slots)
        if (response.data && response.data.id) {
          try {
            if (workHoursData.length > 0) {
              await teachersService.updateAvailability(response.data.id, workHoursData);
              setFormSuccess(`Teacher and work hours saved successfully.${credentialNote}`);
            }
            setTimeout(() => {
              handleCloseDialog();
              fetchTeachers();
            }, 2000);
          } catch (availabilityError) {
            console.error('Error saving work hours:', availabilityError);
            setFormError(`Teacher created but failed to save work hours: ${availabilityError.response?.data?.message || 'Unknown error'}`);
          }
        } else {
          setTimeout(() => {
            handleCloseDialog();
            fetchTeachers();
          }, 2000);
        }
      }
    } catch (error) {
      const data = error.response?.data;
      const apiErrors = Array.isArray(data?.errors) ? data.errors : [];
      const messageParts = [];
      if (data?.message) messageParts.push(data.message);
      if (apiErrors.length) messageParts.push(apiErrors.join('\n'));
      if (!messageParts.length) messageParts.push('Failed to save teacher');
      setFormError(messageParts.join('\n'));
      console.error('Error saving teacher:', error);
    }
  };

  const handleDeleteTeacher = (teacher) => {
    setDeactivateTeacher(teacher);
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Teachers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage teachers and their profiles
          </Typography>
        </Box>
        {canEditTeachers ? (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Teacher
        </Button>
        ) : null}
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, email, phone, country/state/city"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || 'active'}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ height: '100%' }}
            onClick={() => {
              setSearchInput('');
              setFilters({ search: '', status: 'active' });
              setPage(1);
            }}
          >
            Reset
          </Button>
        </Grid>
      </Grid>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Teachers Table */}
      <Card>
        <CardContent>
          {/* Pagination Info at Top */}
          {pagination.total > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} teachers
              </Typography>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={(event, newPage) => {
                  setPage(newPage);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Demo Ready</TableCell>
                  <TableCell>Monthly Demo</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teachers.map((teacher) => (
                  <TableRow key={teacher.id}>
                                         <TableCell>
                       <Box display="flex" alignItems="center">
                         <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                         {teacher.first_name} {teacher.last_name}
                       </Box>
                     </TableCell>
                     <TableCell>{teacher.email}</TableCell>
                     <TableCell>{teacher.phone}</TableCell>
                     <TableCell>
                       {teacher.city && teacher.state ? `${teacher.city}, ${teacher.state}` : teacher.country}
                     </TableCell>
                     <TableCell>
                       <Chip
                         label={teacher.demo_ready || 'No'}
                         color={teacher.demo_ready === 'Yes' ? 'success' : 'default'}
                         size="small"
                       />
                     </TableCell>
                     <TableCell>{teacher.monthly_demo || 'N/A'}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(teacher, 'view')}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {canEditTeachers ? (
                          <>
                            <Tooltip title="Edit Teacher">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(teacher, 'edit')}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {canDeleteTeachers ? (
                            <Tooltip title={filters.status === 'inactive' ? 'Clear leftover assignments' : 'Deactivate teacher'}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteTeacher(teacher)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                            ) : null}
                          </>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.total > 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 3, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" color="text.secondary">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} teachers
                </Typography>
                <Pagination
                  count={pagination.pages}
                  page={pagination.page}
                  onChange={(event, newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit / View Teacher Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'view' ? 'Teacher Details' : editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}
        </DialogTitle>
        <DialogContent>
          {/* Error and Success Messages */}
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {formError}
              </Typography>
            </Alert>
          )}
          {formSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {formSuccess}
            </Alert>
          )}
          
          <fieldset
            disabled={dialogMode === 'view'}
            style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}
          >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name *"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </Grid>
                         <Grid item xs={12} sm={6}>
               <TextField
                 fullWidth
                 label="Email *"
                 type="email"
                 value={formData.email}
                 onChange={(e) => handleInputChange('email', e.target.value)}
                 required
               />
             </Grid>
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth>
                <InputLabel>Country *</InputLabel>
                 <Select
                   value={formData.country}
                   onChange={(e) => handleInputChange('country', e.target.value)}
                  label="Country *"
                  required
                 >
                   <MenuItem value="">Select Country</MenuItem>
                   {getCountries().map((country) => (
                     <MenuItem key={country} value={country}>
                       {country}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth>
                 <InputLabel>State</InputLabel>
                 <Select
                   value={formData.state}
                   onChange={(e) => handleInputChange('state', e.target.value)}
                   label="State"
                   disabled={!formData.country}
                 >
                   <MenuItem value="">{formData.country ? 'Select State' : 'Select Country First'}</MenuItem>
                   {availableStates.map((state) => (
                     <MenuItem key={state} value={state}>
                       {state}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={6}>
               <FormControl fullWidth>
                 <InputLabel>City</InputLabel>
                 <Select
                   value={formData.city}
                   onChange={(e) => handleInputChange('city', e.target.value)}
                   label="City"
                   disabled={!formData.state}
                 >
                   <MenuItem value="">{formData.state ? 'Select City' : 'Select State First'}</MenuItem>
                   {availableCities.map((city) => (
                     <MenuItem key={city} value={city}>
                       {city}
                     </MenuItem>
                   ))}
                 </Select>
               </FormControl>
             </Grid>
             <Grid item xs={12} sm={6}>
               <Box display="flex" gap={1}>
                 <FormControl sx={{ minWidth: 120 }}>
                   <InputLabel>Code</InputLabel>
                   <Select
                     value={formData.phoneCode}
                     onChange={(e) => handleInputChange('phoneCode', e.target.value)}
                     label="Code"
                   >
                     {phoneCodeOptions.map(({ country, code, value }) => (
                       <MenuItem key={value} value={value}>
                         {code} ({country})
                       </MenuItem>
                     ))}
                   </Select>
                 </FormControl>
                 <TextField
                   fullWidth
                   label="Phone"
                   value={formData.phone}
                   onChange={(e) => handleInputChange('phone', e.target.value)}
                   placeholder="Enter phone number"
                 />
               </Box>
             </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Zoom meeting URL"
                placeholder="https://zoom.us/j/..."
                value={formData.zoomMeetingUrl || ''}
                onChange={(e) => handleInputChange('zoomMeetingUrl', e.target.value)}
                helperText="Used for all batches taught by this teacher. Admin/supervisor only."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Demo Ready</InputLabel>
                <Select
                  value={formData.demoReady}
                  onChange={(e) => handleInputChange('demoReady', e.target.value)}
                  label="Demo Ready"
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Demo Countries (Multiple)</InputLabel>
                <Select
                  multiple
                  value={formData.demoCountry || []}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange('demoCountry', typeof value === 'string' ? value.split(',') : value);
                  }}
                  label="Demo Countries (Multiple)"
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return '';
                    return Array.isArray(selected) ? selected.join(', ') : selected;
                  }}
                >
                  {getCountries().map((country) => (
                    <MenuItem key={country} value={country}>
                      <Checkbox checked={formData.demoCountry && formData.demoCountry.indexOf(country) > -1} />
                      <ListItemText primary={country} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Monthly Demo</InputLabel>
                <Select
                  value={formData.monthlyDemo}
                  onChange={(e) => handleInputChange('monthlyDemo', e.target.value)}
                  label="Monthly Demo"
                >
                  <MenuItem value="">Select</MenuItem>
                  <MenuItem value="1-5">1-5</MenuItem>
                  <MenuItem value="6-10">6-10</MenuItem>
                  <MenuItem value="11-15">11-15</MenuItem>
                  <MenuItem value="16-20">16-20</MenuItem>
                  <MenuItem value="20+">20+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Daily Work Hours Section - Multiple Time Slots */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                Daily Work Hours (Multiple Time Slots)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                For shifts that end at midnight (e.g. 8 PM–12 AM), use <strong>11:59 PM</strong> as the end time.
              </Typography>
            </Grid>
            
            {/* Timezone for all work hours */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Work Hours Timezone *</InputLabel>
                <Select
                  value={workHoursTimezone}
                  onChange={(e) => {
                    const newTimezone = e.target.value;
                    setWorkHoursTimezone(newTimezone);
                    // Update timezone in all daily work hours slots
                    setDailyWorkHours(prev => {
                      const updated = {};
                      Object.keys(prev).forEach(day => {
                        updated[day] = {
                          ...prev[day],
                          timezone: newTimezone
                        };
                      });
                      return updated;
                    });
                    const countryFromTz = getCountryFromTimezone(newTimezone);
                    if (countryFromTz) {
                      setFormData(prev => ({
                        ...prev,
                        phoneCode: getPhoneCodeSelectValueForCountry(countryFromTz),
                      }));
                    }
                  }}
                  label="Work Hours Timezone *"
                  required
                >
                  {getAvailableTimezones().map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Day Schedule Sections */}
            {Object.entries(dailyWorkHours).map(([dayName, dayData]) => (
              <Grid item xs={12} key={dayName}>
                <DayScheduleSection
                  dayName={dayName}
                  dayData={dayData}
                  onUpdate={handleUpdateTimeSlot}
                  onAddSlot={handleAddTimeSlot}
                  onDeleteSlot={handleDeleteTimeSlot}
                  disabled={dialogMode === 'view'}
                  />
                </Grid>
            ))}

          </Grid>
          </fieldset>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{dialogMode === 'view' ? 'Close' : 'Cancel'}</Button>
          {dialogMode !== 'view' ? (
            <Button onClick={handleSubmit} variant="contained">
              {editingTeacher ? 'Update' : 'Create'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
      <TeacherDeactivateDialog
        open={Boolean(deactivateTeacher)}
        teacher={deactivateTeacher}
        onClose={() => setDeactivateTeacher(null)}
        onDeactivated={(result) => {
          toast.success(result?.message || 'Teacher deactivated');
          fetchTeachers();
        }}
      />
    </Box>
  );
};

export default Teachers;




